import type {
  PortalAuthChannel,
  PortalConfirmContactByIdentifierPayload,
  PortalConfirmContactPayload,
  PortalContactOtpResponse,
  PortalConversation,
  PortalDocPdfDownloadUrl,
  PortalDocPdfStatus,
  PortalFallbackResponse,
  PortalFaqItem,
  PortalKycInitiateResponse,
  PortalKycStatusResponse,
  PortalListFilters,
  PortalListResponse,
  PortalLoginMagicLinkResponse,
  PortalLoginResponse,
  PortalMagicLinkResponse,
  PortalMagicLinkStatusResponse,
  PortalManifestacaoPayload,
  PortalNotificadoDetail,
  PortalOnboardingPayload,
  PortalOtpPayload,
  PortalOtpResponse,
  PortalRememberHint,
  PortalRequestContactOtpByIdentifierPayload,
  PortalRequestContactOtpPayload,
  PortalTrackingEvent,
  PortalUploadResult,
  PortalValidateHashResponse,
} from '../types/portalTypes';
import type { AbaDoLink } from '../types/notificacaoDetalheTypes';
import type { TokenDeOrigem } from '../types/origemDoAcessoTypes';
import { podeAbrirPortal } from '@/shared/utils/sessaoPortal';
import type { AuthUser } from '@/types/auth/authTypes';

import { apiDelete, apiGet, apiPost } from '../../../shared/utils/requestBuilder';
import { PortalApiError, mensagemDeErroApi } from '../utils/portalError';
import { portalGet, portalPost } from '../utils/portalRequest';

const PORTAL_BASE = '/extrajudicial/portal';
// A WebView do WhatsApp em rede móvel pendura requisição sem responder; sem teto, o
// botão "Baixar notificação" fica em "Baixando..." até recarregar a página.
const PORTAL_DOC_PDF_DOWNLOAD_URL_TIMEOUT_MS = 15_000;

// A origem do envio (`origem`) vai sempre no corpo, nunca no path nem na query da chamada ao core:
// URL de API cai em access log e em `resource.url` do RUM, e o token só serve se não vazar. Sem
// token, `JSON.stringify` omite a chave e o corpo sai igual ao de antes.

const mapAuthUserToPortalUser = (user: AuthUser): PortalLoginResponse['user'] => ({
  id: user.id,
  nome: user.nomeCompleto,
  tipo: user.tipoUsuario,
  pessoaId: user.pessoaId ?? '',
});

export const portalService = {
  validateOnboardingHash: async (
    hash: string,
    sessaoId?: string,
  ): Promise<PortalValidateHashResponse> => {
    const queryParams = sessaoId ? `?sessaoId=${sessaoId}` : '';
    const response = await apiGet(
      `${PORTAL_BASE}/onboarding/validate/${hash}${queryParams}`,
      undefined,
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Link inválido ou expirado' } }));
      throw new Error(errorData.error?.message || 'Link de ativação inválido');
    }

    const raw = await response.json();
    const payload =
      (raw as { data?: PortalValidateHashResponse }).data ?? (raw as PortalValidateHashResponse);
    return payload;
  },

  requestOnboardingOtp: async (
    hash: string,
    payload: PortalOtpPayload,
  ): Promise<PortalOtpResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/onboarding/request-otp/${hash}`, payload, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar código' } }));
      throw new Error(errorData.error?.message || 'Erro ao solicitar código de verificação');
    }

    const raw = await response.json();
    const result = (raw as { data?: PortalOtpResponse }).data ?? (raw as PortalOtpResponse);
    return result;
  },

  activateWithCode: async (
    hash: string,
    code: string,
    tracking?: Omit<PortalTrackingEvent, 'evento'>,
    origem?: TokenDeOrigem,
  ): Promise<PortalLoginResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/onboarding/activate/${hash}`,
      { code, tracking, origem },
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Código inválido ou expirado' } }));
      throw new Error(errorData.error?.message || 'Erro ao ativar conta automaticamente');
    }

    const raw = await response.json();
    const result = (raw as { data?: PortalLoginResponse }).data ?? (raw as PortalLoginResponse);
    return result;
  },

  completeOnboarding: async (
    hash: string,
    payload: PortalOnboardingPayload,
    origem?: TokenDeOrigem,
  ): Promise<PortalLoginResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/onboarding/complete/${hash}`,
      { ...payload, origem },
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao ativar conta' } }));
      throw new Error(errorData.error?.message || 'Erro ao completar ativação');
    }

    const raw = await response.json();
    const result = (raw as { data?: PortalLoginResponse }).data ?? (raw as PortalLoginResponse);
    return result;
  },

  requestLoginOtp: async (payload: PortalOtpPayload): Promise<PortalOtpResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/login/request-otp`, payload, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar código' } }));
      throw new Error(errorData.error?.message || 'Erro ao solicitar código de acesso');
    }

    const raw = await response.json();
    const result = (raw as { data?: PortalOtpResponse }).data ?? (raw as PortalOtpResponse);
    return result;
  },

  verifyLoginOtp: async (payload: PortalOnboardingPayload): Promise<PortalLoginResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/login/verify-otp`, payload, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Código inválido ou expirado' } }));
      throw new Error(errorData.error?.message || 'Erro ao verificar código');
    }

    const raw = await response.json();
    const result = (raw as { data?: PortalLoginResponse }).data ?? (raw as PortalLoginResponse);
    return result;
  },

  getCurrentPortalUser: async (): Promise<PortalLoginResponse['user']> => {
    const response = await apiGet('/auth/me');

    if (!response.ok) {
      throw new Error('Erro ao buscar dados do usuário do portal');
    }

    const raw = await response.json();
    const payload = (raw as { data?: AuthUser }).data ?? (raw as AuthUser);

    if (!podeAbrirPortal(payload)) {
      throw new Error('Sessão do portal inválida');
    }

    return mapAuthUserToPortalUser(payload);
  },

  loginByHash: async (
    hash: string,
    sessaoId?: string,
    origem?: TokenDeOrigem,
  ): Promise<PortalLoginResponse> => {
    const queryParams = sessaoId ? `?sessaoId=${sessaoId}` : '';
    const response = await apiPost(`${PORTAL_BASE}/login/${hash}${queryParams}`, { origem }, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Link inválido ou expirado' } }));
      throw new Error(errorData.error?.message || errorData.error || 'Link de acesso inválido');
    }

    const raw = await response.json();
    const payload = (raw as { data?: PortalLoginResponse }).data ?? (raw as PortalLoginResponse);
    return payload;
  },

  requestMagicLink: async (
    hash: string,
    canalId: string,
    aba?: AbaDoLink,
    origem?: TokenDeOrigem,
  ): Promise<PortalMagicLinkResponse> => {
    // A aba vai no corpo para o core devolvê-la no próprio magic link: quem vem do aviso de
    // resposta da empresa cai nas Mensagens depois de abrir o link no outro aparelho (HU17).
    // A origem vai junto para o core guardá-la no link: o contato escolhido para receber o acesso
    // não substitui o envio cujo link a pessoa abriu.
    const response = await apiPost(
      `${PORTAL_BASE}/onboarding/request-magic-link/${hash}`,
      { canalId, aba, origem },
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar link de acesso' } }));
      throw new Error(errorData.error?.message || 'Erro ao enviar magic link');
    }

    const raw = await response.json();
    return (raw as { data?: PortalMagicLinkResponse }).data ?? (raw as PortalMagicLinkResponse);
  },

  consumeMagicLink: async (token: string): Promise<PortalLoginResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/auth/magic-link/${token}`, {}, true);

    if (!response.ok) {
      const errorData: unknown = await response.json().catch(() => null);
      throw new PortalApiError(
        response.status,
        mensagemDeErroApi(errorData) ?? 'Link de acesso inválido',
      );
    }

    const raw = await response.json();
    return (raw as { data?: PortalLoginResponse }).data ?? (raw as PortalLoginResponse);
  },

  requestFallback: async (
    hash: string,
    currentChannel: PortalAuthChannel,
    reason?: string,
  ): Promise<PortalFallbackResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/onboarding/fallback/${hash}`,
      { currentChannel, reason },
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao solicitar canal alternativo' } }));
      throw new Error(errorData.error?.message || 'Erro ao solicitar fallback');
    }

    const raw = await response.json();
    return (raw as { data?: PortalFallbackResponse }).data ?? (raw as PortalFallbackResponse);
  },

  checkMagicLinkStatus: async (
    hash: string,
    pollToken?: string,
  ): Promise<PortalMagicLinkStatusResponse> => {
    const params = new URLSearchParams({ t: String(Date.now()) });
    if (pollToken) {
      params.set('pollToken', pollToken);
    }
    const response = await apiGet(
      `${PORTAL_BASE}/auth/magic-link-status/${hash}?${params.toString()}`,
      undefined,
      true,
    );

    if (!response.ok) {
      return { status: 'EXPIRED' };
    }

    const raw = await response.json();
    return (
      (raw as { data?: PortalMagicLinkStatusResponse }).data ??
      (raw as PortalMagicLinkStatusResponse)
    );
  },

  // --- KYC ---

  initiateKyc: async (hash: string): Promise<PortalKycInitiateResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/kyc/initiate/${hash}`, {}, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao iniciar verificação facial' } }));
      throw new Error(errorData.error?.message || 'Erro ao iniciar KYC');
    }

    const raw = await response.json();
    return (raw as { data?: PortalKycInitiateResponse }).data ?? (raw as PortalKycInitiateResponse);
  },

  initiateKycByIdentifier: async (identifier: string): Promise<PortalKycInitiateResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/kyc/initiate-by-identifier`,
      { identifier },
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao iniciar verificação facial' } }));
      throw new Error(errorData.error?.message || 'Erro ao iniciar KYC');
    }

    const raw = await response.json();
    return (raw as { data?: PortalKycInitiateResponse }).data ?? (raw as PortalKycInitiateResponse);
  },

  getKycStatus: async (verificationId: string): Promise<PortalKycStatusResponse> => {
    const response = await apiGet(`${PORTAL_BASE}/kyc/status/${verificationId}`, undefined, true);

    if (!response.ok) {
      throw new Error('Erro ao consultar status da verificação');
    }

    const raw = await response.json();
    return (raw as { data?: PortalKycStatusResponse }).data ?? (raw as PortalKycStatusResponse);
  },

  requestLoginMagicLink: async (
    identifier: string,
    canalId: string | null,
    lookupOnly = false,
    redirectTo?: string,
  ): Promise<PortalLoginMagicLinkResponse> => {
    const body: Record<string, unknown> = { identifier };
    if (canalId) body.canalId = canalId;
    if (lookupOnly) body.lookupOnly = true;
    if (redirectTo) body.redirectTo = redirectTo;

    const response = await apiPost(`${PORTAL_BASE}/login/request-magic-link`, body, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar link de acesso' } }));
      throw new Error(errorData.error?.message || 'Erro ao solicitar link de acesso');
    }

    const raw = await response.json();
    return (
      (raw as { data?: PortalLoginMagicLinkResponse }).data ?? (raw as PortalLoginMagicLinkResponse)
    );
  },

  setLoginRemember: async (identifier: string): Promise<{ success: true }> => {
    const response = await apiPost(`${PORTAL_BASE}/login/remember`, { identifier }, true);
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao lembrar acesso' } }));
      throw new Error(errorData.error?.message || 'Erro ao lembrar acesso');
    }
    return { success: true };
  },

  getLoginRememberHint: async (): Promise<PortalRememberHint | null> => {
    const response = await apiGet(`${PORTAL_BASE}/login/remember/hint`, undefined, true);
    if (!response.ok) return null;
    const raw = await response.json();
    const data = (raw as { data?: { hint: PortalRememberHint | null } }).data;
    return data?.hint ?? null;
  },

  clearLoginRemember: async (): Promise<{ success: true }> => {
    await apiDelete(`${PORTAL_BASE}/login/remember`, true);
    return { success: true };
  },

  useLoginRemember: async (
    canalId: string | null,
    redirectTo?: string,
  ): Promise<PortalLoginMagicLinkResponse> => {
    const body: Record<string, unknown> = {};
    if (canalId) body.canalId = canalId;
    if (redirectTo) body.redirectTo = redirectTo;
    const response = await apiPost(`${PORTAL_BASE}/login/remember/use`, body, true);
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao usar acesso lembrado' } }));
      throw new Error(errorData.error?.message || 'Erro ao usar acesso lembrado');
    }
    const raw = await response.json();
    return (
      (raw as { data?: PortalLoginMagicLinkResponse }).data ?? (raw as PortalLoginMagicLinkResponse)
    );
  },

  requestContactOtp: async (
    hash: string,
    payload: PortalRequestContactOtpPayload,
  ): Promise<PortalContactOtpResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/kyc/request-contact-otp/${hash}`, payload, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar código para o novo contato' } }));
      throw new Error(errorData.error?.message || 'Erro ao enviar código para o novo contato');
    }

    const raw = await response.json();
    return (raw as { data?: PortalContactOtpResponse }).data ?? (raw as PortalContactOtpResponse);
  },

  confirmContact: async (
    hash: string,
    payload: PortalConfirmContactPayload,
  ): Promise<PortalContactOtpResponse> => {
    const response = await apiPost(`${PORTAL_BASE}/kyc/confirm-contact/${hash}`, payload, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao confirmar novo contato' } }));
      throw new Error(errorData.error?.message || 'Erro ao confirmar novo contato');
    }

    const raw = await response.json();
    return (raw as { data?: PortalContactOtpResponse }).data ?? (raw as PortalContactOtpResponse);
  },

  requestContactOtpByIdentifier: async (
    payload: PortalRequestContactOtpByIdentifierPayload,
  ): Promise<PortalContactOtpResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/kyc/request-contact-otp-by-identifier`,
      payload,
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao enviar código para o novo contato' } }));
      throw new Error(errorData.error?.message || 'Erro ao enviar código para o novo contato');
    }

    const raw = await response.json();
    return (raw as { data?: PortalContactOtpResponse }).data ?? (raw as PortalContactOtpResponse);
  },

  confirmContactByIdentifier: async (
    payload: PortalConfirmContactByIdentifierPayload,
  ): Promise<PortalContactOtpResponse> => {
    const response = await apiPost(
      `${PORTAL_BASE}/kyc/confirm-contact-by-identifier`,
      payload,
      true,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao confirmar novo contato' } }));
      throw new Error(errorData.error?.message || 'Erro ao confirmar novo contato');
    }

    const raw = await response.json();
    return (raw as { data?: PortalContactOtpResponse }).data ?? (raw as PortalContactOtpResponse);
  },

  logout: async (): Promise<void> => {
    const response = await portalPost(`${PORTAL_BASE}/logout`, undefined, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao fazer logout do portal' } }));
      throw new Error(errorData.error?.message || 'Erro ao fazer logout do portal');
    }
  },

  trackEvent: async (itemId: string | undefined, event: PortalTrackingEvent): Promise<void> => {
    const url = itemId ? `${PORTAL_BASE}/track/${itemId}` : `${PORTAL_BASE}/track`;
    const response = await apiPost(url, event, true);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao registrar evento' } }));
      throw new Error(errorData.error?.message || 'Erro ao registrar evento');
    }
  },

  listNotificacoes: async (
    filters: PortalListFilters,
    sessaoId?: string,
  ): Promise<PortalListResponse> => {
    const params = new URLSearchParams();
    if (sessaoId) params.set('sessaoId', sessaoId);
    params.set('page', String(filters.page));
    params.set('pageSize', String(filters.pageSize));
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.dataInicio) params.set('dataInicio', filters.dataInicio);
    if (filters.dataFim) params.set('dataFim', filters.dataFim);

    const queryString = params.toString();
    const response = await portalGet(`${PORTAL_BASE}/notificacoes?${queryString}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao listar notificações' } }));
      throw new Error(errorData.error?.message || 'Erro ao listar notificações');
    }

    const raw = await response.json();
    const payload = (raw as { data?: PortalListResponse }).data ?? (raw as PortalListResponse);
    return payload;
  },

  getDetail: async (
    id: string,
    sessaoId?: string,
    trackingData?: Omit<PortalTrackingEvent, 'evento'>,
  ): Promise<PortalNotificadoDetail> => {
    const queryParams = sessaoId ? `?sessaoId=${sessaoId}` : '';
    const headers: Record<string, string> = {};
    if (trackingData) {
      headers['x-portal-tracking'] = JSON.stringify(trackingData);
    }
    const response = await portalGet(
      `${PORTAL_BASE}/notificacoes/${id}${queryParams}`,
      undefined,
      false,
      headers,
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao obter detalhes' } }));
      throw new Error(errorData.error?.message || 'Erro ao obter detalhes da notificação');
    }

    const raw = await response.json();
    const payload =
      (raw as { data?: PortalNotificadoDetail }).data ?? (raw as PortalNotificadoDetail);
    return payload;
  },

  trackView: async (itemId: string, docId: string): Promise<void> => {
    const response = await portalPost(`${PORTAL_BASE}/notificacoes/${itemId}/track-view/${docId}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao registrar visualização' } }));
      throw new Error(errorData.error?.message || 'Erro ao registrar visualização');
    }
  },

  responder: async (itemId: string, payload: PortalManifestacaoPayload): Promise<unknown> => {
    const response = await portalPost(`${PORTAL_BASE}/notificacoes/${itemId}/responder`, payload);

    if (!response.ok) {
      const errorData: unknown = await response.json().catch(() => null);
      throw new PortalApiError(response.status, mensagemDeErroApi(errorData));
    }

    const raw = await response.json();
    return (raw as { data?: unknown }).data ?? raw;
  },

  uploadAnexo: async (itemId: string, file: File): Promise<PortalUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await portalPost(`${PORTAL_BASE}/notificacoes/${itemId}/upload`, formData);

    if (!response.ok) {
      const errorData: unknown = await response.json().catch(() => null);
      throw new PortalApiError(response.status, mensagemDeErroApi(errorData));
    }

    const raw = await response.json();
    const payload = (raw as { data?: PortalUploadResult }).data ?? (raw as PortalUploadResult);
    return payload;
  },

  downloadDocumento: async (documentUrl: string): Promise<string> => {
    if (/^https?:\/\//.test(documentUrl)) {
      return documentUrl;
    }

    const endpoint = documentUrl.startsWith('/api/')
      ? documentUrl.replace(/^\/api/, '')
      : documentUrl;
    const response = await portalGet(endpoint);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao obter download' } }));
      throw new Error(errorData.error?.message || 'Erro ao obter URL de download');
    }

    const raw = await response.json();
    const payload = (raw as { data?: { url: string } }).data ?? (raw as { url: string });
    return payload.url;
  },

  getDocumentPdfContentUrl: (itemId: string): string => {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8081/api').replace(
      /\/$/,
      '',
    );
    return `${baseUrl}${PORTAL_BASE}/notificacoes/${itemId}/documento-pdf/content`;
  },

  getDocumentPdfStatus: async (itemId: string): Promise<PortalDocPdfStatus> => {
    const response = await portalGet(`${PORTAL_BASE}/notificacoes/${itemId}/documento-pdf`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao obter status do documento' } }));
      throw new Error(errorData.error?.message || 'Erro ao obter status do documento');
    }

    const raw = await response.json();
    return (raw as { data?: PortalDocPdfStatus }).data ?? (raw as PortalDocPdfStatus);
  },

  getDocumentPdfDownloadUrl: async (itemId: string): Promise<string> => {
    const response = await portalGet(
      `${PORTAL_BASE}/notificacoes/${itemId}/documento-pdf/download-url`,
      undefined,
      false,
      undefined,
      PORTAL_DOC_PDF_DOWNLOAD_URL_TIMEOUT_MS,
    );

    if (!response.ok) {
      const errorData: unknown = await response.json().catch(() => null);
      throw new PortalApiError(response.status, mensagemDeErroApi(errorData));
    }

    const raw = await response.json();
    const payload =
      (raw as { data?: PortalDocPdfDownloadUrl }).data ?? (raw as PortalDocPdfDownloadUrl);
    // Sem URL, `baixarArquivoDeUrl` abriria uma aba em branco em vez de falhar.
    if (typeof payload?.url !== 'string' || payload.url.length === 0) {
      throw new PortalApiError(response.status, 'Documento ainda não disponível.');
    }
    return payload.url;
  },

  getHelpConversation: async (itemId: string, sessaoId?: string): Promise<PortalConversation> => {
    const queryParams = sessaoId ? `?sessaoId=${sessaoId}` : '';
    const response = await portalGet(`${PORTAL_BASE}/notificacoes/${itemId}/ajuda${queryParams}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao abrir ajuda' } }));
      throw new Error(errorData.error?.message || 'Erro ao abrir conversa de ajuda');
    }

    const raw = await response.json();
    const payload = (raw as { data?: PortalConversation }).data ?? (raw as PortalConversation);
    return payload;
  },

  getFaq: async (): Promise<PortalFaqItem[]> => {
    const response = await portalGet(`${PORTAL_BASE}/faq`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: 'Erro ao carregar FAQ' } }));
      throw new Error(errorData.error?.message || 'Erro ao carregar perguntas frequentes');
    }

    const raw = await response.json();
    const payload = (raw as { data?: PortalFaqItem[] }).data ?? (raw as PortalFaqItem[]);
    return payload;
  },
};
